import React from "react";
import { Table, TableBody, TableHead, TableRow, TableCell } from "@mui/material";
import { LocalPharmacy as LocalPharmacyIcon } from "@mui/icons-material";
import { DataTableCard } from "../../components/organisms";
import {
  ClickableRow,
  DateCell,
  EDGE_L,
  EDGE_R,
  FillerRow,
  PatientCell,
  PlainHead,
  ROWS_PER_PAGE_OPTIONS,
  SkeletonRows,
  SortHead,
  StatusPill,
  TextCell,
  cellSx,
} from "../../components/organisms/ClinicRecordList";
import { useClinicTheme } from "../../theme";
import { PRESCRIPTION_STATUS, getPrescriptionStatus, diagnosisNames, medicationNames } from "./constants";
import useTerms from "../../hooks/useTerms";

const COLS = 5;
export const dateOf = (t) => t.createdInfo || t.createdAt;

export default function PrescriptionsTable({ list, isLoading, empty, onOpenDetail }) {
  const terms = useTerms();
  const { palette } = useClinicTheme();
  const sortProps = { sort: list.sort, dir: list.dir, onSort: list.onSort };

  return (
    <DataTableCard
      loading={Boolean(isLoading)}
      variant="flat"
      empty={!isLoading && list.filtered.length === 0}
      emptyIcon={<LocalPharmacyIcon />}
      emptyTitle={empty.title}
      emptyMessage={empty.message}
      page={list.page}
      rowsPerPage={list.rowsPerPage}
      totalCount={list.filtered.length}
      onPageChange={list.onPageChange}
      onRowsPerPageChange={list.onRowsPerPageChange}
      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
    >
      <Table stickyHeader size="small" sx={{ tableLayout: "fixed", minWidth: 960 }}>
        <TableHead>
          <TableRow>
            <SortHead field="name" label={terms.patient} {...sortProps} sx={{ ...EDGE_L, width: "26%" }} />
            <PlainHead sx={{ width: "24%" }}>Tanı</PlainHead>
            <PlainHead sx={{ width: "26%" }}>İlaçlar</PlainHead>
            <SortHead field="date" label="Tarih" defaultDir="desc" {...sortProps} sx={{ width: "12%" }} />
            <PlainHead sx={{ ...EDGE_R, width: "12%" }}>Durum</PlainHead>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <SkeletonRows rows={Math.min(10, list.rowsPerPage)} cols={COLS} />
          ) : list.pagedRows.length ? (
            list.pagedRows.map((row) => {
              const statusKey = getPrescriptionStatus(row);
              const diag = diagnosisNames(row);
              const meds = medicationNames(row);
              const note = String(row.prescriptionNote || "").trim();
              return (
                <ClickableRow key={row._id} onClick={() => onOpenDetail(row)} palette={palette}>
                  <PatientCell patient={row.user} />
                  <TextCell
                    main={diag[0]}
                    sub={diag.length > 1 ? `+${diag.length - 1} tanı daha` : null}
                    tooltip={diag.length > 1 ? diag.join(", ") : null}
                  />
                  <TextCell
                    main={meds.length ? meds.slice(0, 2).join(", ") : null}
                    sub={meds.length > 2 ? `+${meds.length - 2} ilaç daha` : note || null}
                    tooltip={meds.length > 2 ? meds.join(", ") : null}
                  />
                  <DateCell value={dateOf(row)} />
                  <TableCell sx={{ ...cellSx, ...EDGE_R }}>
                    <StatusPill
                      label={PRESCRIPTION_STATUS[statusKey].label}
                      tone={PRESCRIPTION_STATUS[statusKey].tone}
                      palette={palette}
                    />
                  </TableCell>
                </ClickableRow>
              );
            })
          ) : (
            <FillerRow cols={COLS} />
          )}
        </TableBody>
      </Table>
    </DataTableCard>
  );
}
